import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

export const FINE_POINTER_QUERY = "(hover: hover) and (pointer: fine)";

function matches(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(query).matches;
}

export function useMediaQuery(query: string): boolean {
  const [value, setValue] = useState(() => matches(query));

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const list = window.matchMedia(query);
    const update = () => setValue(list.matches);
    update();
    if (typeof list.addEventListener === "function") {
      list.addEventListener("change", update);
      return () => list.removeEventListener("change", update);
    }
    list.addListener(update);
    return () => list.removeListener(update);
  }, [query]);

  return value;
}

export const usePrefersReducedMotion = () => useMediaQuery(REDUCED_MOTION_QUERY);

export const useFinePointer = () => useMediaQuery(FINE_POINTER_QUERY);

export function useTilt<T extends HTMLElement>(maxDegrees = 5) {
  const reduced = usePrefersReducedMotion();
  const finePointer = useFinePointer();
  const enabled = finePointer && !reduced;
  const ref = useRef<T | null>(null);
  const frame = useRef<number | null>(null);

  const reset = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    const node = ref.current;
    if (!node) return;
    node.style.setProperty("--tilt-x", "0deg");
    node.style.setProperty("--tilt-y", "0deg");
    node.style.setProperty("--glare-x", "50%");
    node.style.setProperty("--glare-y", "0%");
    node.removeAttribute("data-tilting");
  }, []);

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<T>) => {
      if (!enabled || event.pointerType === "touch") return;
      const node = ref.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        node.style.setProperty("--tilt-x", `${((x - 0.5) * 2 * maxDegrees).toFixed(2)}deg`);
        node.style.setProperty("--tilt-y", `${((0.5 - y) * 2 * maxDegrees).toFixed(2)}deg`);
        node.style.setProperty("--glare-x", `${Math.round(x * 100)}%`);
        node.style.setProperty("--glare-y", `${Math.round(y * 100)}%`);
        node.setAttribute("data-tilting", "true");
      });
    },
    [enabled, maxDegrees],
  );

  useEffect(() => {
    if (!enabled) reset();
  }, [enabled, reset]);

  useEffect(() => () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
  }, []);

  const handlers = enabled ? { onPointerMove, onPointerLeave: reset } : {};

  return { ref, enabled, handlers };
}
